import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableEnergyStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportenergyStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  fetchEnergyReportStation,
  setReportFilterData,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setSortData,
  setEnergyStationReportUpdate,
} from '../../../../redux/slice/ReportEnergyStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slice/ReportSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationViewSidePanel from '../../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/report';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter?: any;
  clearDateRange?: () => void;
}

/**
 * Reports management page
 */
function EnergyReportTableStation(properties: ReportTableProperties) {
  const { leftTopContent, preferenceFilter, clearDateRange } = properties;

  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isEnergyStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state: RootState) => state.reportEnergyStation);
  const reportsData = useAppSelector((state: RootState) => state.reportData);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);

  const column = [
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableEnergyStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableEnergyStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableEnergyStationReport,
      sortAllow: true,
    },
    {
      powerConsumed: 'Power Consumed',
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * Function to show power consumed in kW
       * @param rowData
       * @returns
       */
      customData: (rowData: AvailableEnergyStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue((rowData?.powerConsumed || 0) / 1000)}
          </p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('basePricingEnergyConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * Function to show power consumed in kW
       * @param rowData
       * @returns
       */
      customData: (rowData: AvailableEnergyStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue((rowData?.basePricePowerConsumed || 0) / 1000)}
          </p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('peakPricingEnergyConsumption'),
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * Function to show power consumed in kW
       * @param rowData
       * @returns
       */
      customData: (rowData: AvailableEnergyStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue((rowData?.peakPricePowerConsumed || 0) / 1000)}
          </p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('offPeakPricingEnergyConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * Function to show power consumed in kW
       * @param rowData
       * @returns
       */
      customData: (rowData: AvailableEnergyStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue((rowData?.offPeakPricePowerConsumed || 0) / 1000)}
          </p>
          <span>kWh</span>
        </div>
      ),
    },
    {
      label: t('scheduleChargeEnergyConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
    },
  ];

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchEnergyReportStation({
        ...queryParameters,
        sortField,
        sortBy,
        page: pageCount,
        limit,
      }),
    );
  };

  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setReportFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    dispatch(
      fetchEnergyReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchEnergyReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit: limitCount,
        sortField,
        sortBy,
      }),
    );
  };
  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchEnergyReportStation({
        ...queryParameters,
        sortBy,
        sortField,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page,
        limit,
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationInfo'), accessorKey: 'chargingStationName' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('basePricingEnergyConsumption'), accessorKey: 'basePricePowerConsumed' },
    { label: t('peakPricingEnergyConsumption'), accessorKey: 'peakPricePowerConsumed' },
    { label: t('offPeakPricingEnergyConsumption'), accessorKey: 'offPeakPricePowerConsumed' },
    { label: t('scheduleChargeEnergyConsumption'), accessorKey: 'scheduledPowerConsumed' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const exportParameters = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    };
    reportApi
      .fetchEnergicStationListExport({ ...exportParameters })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AvailableEnergyStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'chargingStationName':
                return `${row.chargingStationName} ${ConvertUTCtoDate(row.date)} ${row?.chargingStationLocation ?? ''}`;
              case 'deviceCount':
                return row?.deviceCount ?? 0;
              case 'powerConsumed':
                return `${ToFixedValue(row.powerConsumed / 1000) ?? 0} kWh`;
              case 'basePricePowerConsumed':
                return `${ToFixedValue(row.basePricePowerConsumed / 1000) ?? 0} kWh`;
              case 'peakPricePowerConsumed':
                return `${ToFixedValue(row.peakPricePowerConsumed / 1000) ?? 0} kWh`;
              case 'offPeakPricePowerConsumed':
                return `${ToFixedValue(row.offPeakPricePowerConsumed / 1000) ?? 0} kWh`;
              case 'scheduledPowerConsumed':
                return row?.scheduledPowerConsumed ?? 0;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('reportsList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('reportsList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchEnergyReportStation({
          ...data,
        }),
      );
    }
  };
  const fetchReportData = useCallback(() => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);
    if (!selectedFacility?._id) return;

    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    currentFacilityTagsList,
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isEnergyStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setEnergyStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isEnergyStationReportUpdate]);

  useEffect(() => {
    if (filters.length === 1) {
      dispatch(fetchListFilters());
    }
    if (preferenceFilter.pageName === 'energyReport' && reportsData.reportByType === 'station') {
      if (filters.length > 1) {
        const setFilter = filters.map((filter) => {
          let returnValue = filter;
          const single = preferenceFilter.filter?.find(
            (preference: any) => preference.field === filter.key,
          );
          if (single && single?.value.length > 0) {
            returnValue = {
              ...returnValue,
              value: single?.value,
              applyFilter: true,
              valueToShow: returnValue?.listOfValue
                ?.filter((show) => single?.value?.includes(show.value))
                .map((show) => show.label),
            };
          } else {
            returnValue = {
              ...returnValue,
              value: [],
              applyFilter: false,
              valueToShow: [],
            };
          }
          return returnValue;
        });
        dispatch(setReportFiltersData(setFilter));
        const queryParameters = GetParametersFromCommonFilters(setFilter);
        fetchData({
          ...queryParameters,
          startDate: reportsData.reportFilter.startDate,
          endDate: reportsData.reportFilter.endDate,
          page: DefaultApiLimit.page,
          limit,
        });
      }
    }
  }, [preferenceFilter]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          tableHeight="calc(100vh - 335px)"
          version="v2"
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          rightTopContent={leftTopContent}
          refreshCurrentPages={ReferenceCurrentPages}
          serialNo
          onClearFilterChange={() => {
            if (clearDateRange) {
              clearDateRange();
            }
          }}
          onHiddenColumnsChange={setHiddenColumns}
          flexDirection
        />
      </div>
    </div>
  );
}

export default EnergyReportTableStation;
